import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, Calendar, ChevronLeft, ChevronRight, Clock,
  Copy, Edit, Eye, Hourglass, Plus, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { EmptyState, MetricStrip } from "@/components/CommandSystem";
import { RegisterSearch } from "@/components/RegisterSearch";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission, hasPermission } from "@/lib/permissions";
import { inr, fmtDate } from "@/lib/format";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

import {
  STATUS_BADGE, ROWS, BATCH, EMPTY,
  asArray, normalizeCampaign,
  expiryClass,
  chargeTotal, defaultChargeForHoarding, latestBusinessMargins, validateCampaignMargins,
} from "./utils";

function dateOnlyValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function rangesOverlap(startA, endA, startB, endB) {
  return startA != null && endA != null && startB != null && endB != null && startA <= endB && endA >= startB;
}

function ContextMenuItem({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.();
      }}
      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

export function Campaigns() {
  const { user } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [batchOffset, setBatchOffset] = useState(0);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [hoardings, setHoardings] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ status: searchParams.get("status") || "", client: "" });
  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [hoardingSearch, setHoardingSearch] = useState("");
  const [businessMargins, setBusinessMargins] = useState([]);
  const [accountingSettings, setAccountingSettings] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const canCreate = hasPermission(user, "create_campaigns");
  const canEditCampaign = hasAnyPermission(user, ["edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns", "modify_records"]);
  const canViewRevenue = hasAnyPermission(user, ["view_campaign_amounts", "view_basic_revenue", "view_profit_and_loss", "view_financial_reports"]);
  const canAssignHoardings = hasPermission(user, "assign_hoardings_to_campaign");
  const canChangeDates = hasPermission(user, "change_campaign_dates");
  const canEditAmounts = hasAnyPermission(user, ["edit_campaign_amounts", "change_campaign_amounts"]);
  const isFirstSearch = useRef(true);
  const isFirstFilterMount = useRef(true);
  const consumedCreateState = useRef(false);
  const consumedEditState = useRef(false);

  const fetchBatch = useCallback(async (offset, searchQ, currentFilters, reset = false) => {
    setLoadingCampaigns(true);
    try {
      const params = new URLSearchParams({ limit: BATCH, offset });
      if (searchQ) params.set("q", searchQ);
      if (currentFilters.status) params.set("status", currentFilters.status);
      if (currentFilters.client) params.set("client_id", currentFilters.client);
      const { data } = await api.get(`/campaigns?${params}`, { skipGlobalLoader: true });
      const rawList = Array.isArray(data) ? data : asArray(data.campaigns);
      const dataTotal = Array.isArray(data) ? rawList.length : (data.total ?? rawList.length);
      const newItems = rawList.map(normalizeCampaign);
      setTotal(dataTotal);
      if (reset) { setAllCampaigns(newItems); setBatchOffset(newItems.length); setPage(1); }
      else { setAllCampaigns((prev) => [...prev, ...newItems]); setBatchOffset((prev) => prev + newItems.length); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  }, []);

  const refreshSummaries = useCallback(() => {
    api.get("/campaigns-summary/kpis", { skipGlobalLoader: true }).then((r) => setKpis(r.data || {})).catch(() => setKpis({}));
  }, []);

  useEffect(() => {
    fetchBatch(0, "", filters, true);
    api.get("/campaigns/init", { skipGlobalLoader: true }).then((r) => {
      const d = r.data;
      setKpis(d.kpis || {});
      setHoardings(Array.isArray(d.hoardings) ? d.hoardings : []);
      setClients(Array.isArray(d.clients) ? d.clients : []);
      setBusinessMargins(Array.isArray(d.margins) ? d.margins : []);
    }).catch(() => {
      setKpis({});
      setHoardings([]);
      setClients([]);
      setBusinessMargins([]);
    });
    api.get("/accounts/settings", { skipGlobalLoader: true })
      .then(({ data }) => setAccountingSettings(data || null))
      .catch(() => setAccountingSettings(null));
  }, [fetchBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFirstSearch.current) { isFirstSearch.current = false; return; }
    fetchBatch(0, committedQ, filters, true);
  }, [committedQ, fetchBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // When server-side filters change, reload from page 1 (skip initial mount)
  useEffect(() => {
    if (isFirstFilterMount.current) { isFirstFilterMount.current = false; return; }
    fetchBatch(0, committedQ, filters, true);
    setPage(1);
  }, [filters.status, filters.client]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const onKeyDown = (event) => { if (event.key === "Escape") close(); };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  const endingFilter = searchParams.get("ending") || "";
  const sorted = useMemo(() => {
    if (!endingFilter) return allCampaigns;
    const todayValue = dateOnlyValue(new Date());
    return allCampaigns.filter((campaign) => {
      const endValue = dateOnlyValue(campaign.end_date);
      if (endValue == null || todayValue == null) return false;
      const days = Math.round((endValue - todayValue) / 86400000);
      if (endingFilter === "today") return days === 0;
      if (endingFilter === "7d") return days > 0 && days <= 7;
      return true;
    });
  }, [allCampaigns, endingFilter]);

  const totalDisplayPages = Math.max(1, Math.ceil(sorted.length / ROWS));
  const pageItems = sorted.slice((page - 1) * ROWS, page * ROWS);

  function goToPage(newPage) {
    if (newPage < 1 || newPage > totalDisplayPages) return;
    setPage(newPage);
    const loadedPages = Math.ceil(allCampaigns.length / ROWS);
    if (newPage >= loadedPages - 1 && allCampaigns.length < total && !loadingCampaigns) {
      fetchBatch(batchOffset, committedQ, filters);
    }
  }

  useEffect(() => {
    const prefill = location.state?.createCampaign;
    if (prefill && canCreate) {
      nav("/campaigns/new", { replace: true, state: { createCampaign: prefill } });
    }
  }, [location.state, canCreate, nav]);

  useEffect(() => {
    const editID = location.state?.editCampaignID;
    if (editID) {
      nav(`/campaigns/${editID}/edit`, { replace: true });
    }
  }, [location.state, nav]);

  function commitSearch() {
    const nextQuery = q.trim();
    setCommittedQ((cur) => cur === nextQuery ? cur : nextQuery);
    setPage(1);
  }

  function openRowMenu(event, campaign) {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      x: Math.min(event.clientX, window.innerWidth - 240),
      y: Math.min(event.clientY, window.innerHeight - 190),
      campaign,
    });
  }

  async function copyCampaignId(campaign) {
    const value = campaign?.campaign_id || "";
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Campaign ID copied");
    } catch {
      toast.error("Campaign ID could not be copied");
    } finally {
      setContextMenu(null);
    }
  }

  return (
    <Layout
      title="Campaigns & Bookings"
      subtitle="Plan, assign hoardings, and track every campaign lifecycle"
      actions={
        canCreate ? (
          <button onClick={() => nav("/campaigns/new")} data-testid="campaigns-new-btn" className="hx-primary-action">
            <Plus size={15} /> New Campaign
          </button>
        ) : null
      }
    >
      <div className="hx-hoardings-page hx-campaigns-page">
        {kpis && (
          <div data-testid="campaigns-kpis">
            <MetricStrip
              metrics={[
                { label: "Total", value: kpis.total, icon: Calendar, tone: "indigo" },
                { label: "Active", value: kpis.active, icon: Activity, tone: "emerald" },
                { label: "Expiring 7d", value: kpis.expiring_this_week, icon: Hourglass, tone: "amber" },
                { label: "Reserved", value: kpis.reserved_hoardings, icon: Clock, tone: "blue" },
                canViewRevenue && { label: "Revenue", value: inr(kpis.campaign_revenue), icon: Wallet, tone: "slate" },
              ].filter(Boolean)}
            />
          </div>
        )}

        <section className="hx-inventory-register" aria-busy={loadingCampaigns}>
          <header>
            <div>
              <h2>Campaign Register</h2>
              <p>
                {sorted.length} result{sorted.length !== 1 ? "s" : ""}
                {allCampaigns.length < total && ` · ${total} total in DB`}
              </p>
            </div>
            <div className="hx-register-controls">
              <RegisterSearch
                value={q}
                onChange={setQ}
                onSearch={commitSearch}
                placeholder="Search ID, name, client..."
                testId="campaigns-search"
                ariaLabel="Search campaigns"
              />
              {loadingCampaigns && (
                <span className="hx-register-refresh" role="status">
                  <span aria-hidden="true" />
                  Updating
                </span>
              )}
            </div>
          </header>

          <div className="hx-register-filterbar">
            <label className="hx-register-filter">
              <span>Status</span>
              <Select value={filters.status || "all"} onValueChange={(v) => { setFilters({ ...filters, status: v === "all" ? "" : v }); setPage(1); }}>
                <SelectTrigger data-testid="filter-camp-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="FixedDate">Fixed-date campaigns</SelectItem>
                  <SelectItem value="OpenEnded">Open-ended campaigns</SelectItem>
                  <SelectItem value="UnknownEndDate">Unknown end date</SelectItem>
                  <SelectItem value="UnknownPOValue">Unknown PO value</SelectItem>
                  {Object.keys(STATUS_BADGE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="hx-register-filter is-wide">
              <span>Client</span>
              <Select value={filters.client || "all"} onValueChange={(v) => { setFilters({ ...filters, client: v === "all" ? "" : v }); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All clients</SelectItem>
                  {clients.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          </div>

          <div className={`hx-inventory-table-wrap hx-campaign-register-table ${loadingCampaigns ? "is-refreshing" : ""}`}>
          <table>
            <thead>
              <tr>
                <th className="is-id">ID</th>
                <th className="is-hoarding">Client / Campaign</th>
                <th className="is-count">Sites</th>
                <th className="is-date">Start</th>
                <th className="is-date">End</th>
                <th className="is-days">Left</th>
                <th className="is-status">Status</th>
                {canViewRevenue && <th className="is-revenue">Revenue</th>}
              </tr>
            </thead>
            <tbody>
              {pageItems.map((c) => (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-testid={`campaign-row-${c.id}`}
                  onClick={() => {
                    nav(`/campaigns/${c.id}`);
                  }}
                  onContextMenu={(event) => openRowMenu(event, c)}
                >
                  <td className="font-mono text-[11px] font-semibold text-slate-500">{c.campaign_id}</td>
                  <td>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); nav(`/campaigns/${c.id}`); }}
                      className="hx-campaign-title-link"
                    >
                      {c.name}
                    </button>
                    <div className="hx-campaign-client">{c.client_name || "No client"}</div>
                    <div className="mt-1 hidden flex-wrap gap-1 xl:flex">
                      {c.is_open_ended && <span className="hx-status-pill tone-amber">No confirmed end date</span>}
                      {c.po_value_status === "unknown" && <span className="hx-status-pill tone-indigo">PO unknown</span>}
                      {c.po_value_status === "estimated" && <span className="hx-status-pill tone-blue">PO estimated</span>}
                    </div>
                  </td>
                  <td className="text-center font-mono font-semibold text-slate-700">{c.hoarding_count}</td>
                  <td className="hx-date-cell">{fmtDate(c.start_date)}</td>
                  <td className={`hx-date-cell ${expiryClass(c)}`}>{c.is_open_ended ? "Open ended" : fmtDate(c.end_date)}</td>
                  <td className={`hx-date-cell ${expiryClass(c)}`}>
                    {c.status === "Completed" ? "Done" : c.remaining_days != null ? `${c.remaining_days}d` : "—"}
                  </td>
                  <td>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_BADGE[c.status] || ""}`}>
                      {c.status}
                    </span>
                  </td>
                  {canViewRevenue && <td className="hx-price-cell">{inr(c.revenue)}</td>}
                </motion.tr>
              ))}
              {pageItems.length === 0 && (
                <tr className="hx-empty-table-row">
                  <td colSpan={canViewRevenue ? 8 : 7} className="py-10 text-center text-slate-500">
                    {loadingCampaigns ? "Loading…" : <EmptyState title="No campaigns match these filters" description="Try changing the status, client, or search term." />}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalDisplayPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
            <span className="text-xs text-slate-500">
              {sorted.length} result{sorted.length !== 1 ? "s" : ""}
              {allCampaigns.length < total && ` · ${total} total in DB`}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => goToPage(page - 1)} disabled={page === 1} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(7, totalDisplayPages) }, (_, i) => {
                const p = totalDisplayPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
                if (p < 1 || p > totalDisplayPages) return null;
                return (
                  <button key={p} onClick={() => goToPage(p)} className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all ${p === page ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => goToPage(page + 1)} disabled={page === totalDisplayPages} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
        </section>
      </div>
      {contextMenu && (
        <div
          className="fixed z-[2100] w-56 rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-950/15"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div className="border-b border-slate-100 px-3 py-2">
            <div className="truncate text-xs font-bold text-slate-900">{contextMenu.campaign.campaign_id}</div>
            <div className="truncate text-[11px] text-slate-500">{contextMenu.campaign.name}</div>
          </div>
          <div className="mt-1 space-y-0.5">
            <ContextMenuItem icon={Eye} label="View Details" onClick={() => { setContextMenu(null); nav(`/campaigns/${contextMenu.campaign.id}`); }} />
            {canEditCampaign && (
              <ContextMenuItem icon={Edit} label="Edit Campaign" onClick={() => { setContextMenu(null); nav(`/campaigns/${contextMenu.campaign.id}/edit`, { state: { campaign: contextMenu.campaign } }); }} />
            )}
            <ContextMenuItem icon={Copy} label="Copy Campaign ID" onClick={() => copyCampaignId(contextMenu.campaign)} />
          </div>
        </div>
      )}
    </Layout>
  );
}
